import React, { useEffect, useState, useMemo } from "react";
import {
	Row,
	Col,
	Card,
	Tag,
	Space,
	Typography,
	Button,
	Drawer,
	List,
	message,
	Select,
	Modal,
	Progress,
} from "antd";
import { LeftOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
	get_agent_client_route_tags,
	get_agent_pos_selection_ids,
	get_agent_pos_with_last_conv,
	get_client_templates,
	get_conversation_messages_page,
	get_conversation_phases,
	get_pos_conversations_page,
	send_template_to_pos,
	sync_client_templates,
} from "../../../../../api/endpoints/agents";
import AntdSearch from "../../../../common/antd-search/AntdSearch";
import { CONSTANTS } from "../../../../../utils/constants";
import {
	AntdInput,
	AntdTable,
	CardContainer,
	Circle,
	CompaginationDiv,
	Container,
	FilterActions,
	FilterContainer,
	Inner,
} from "./styles";
import { COLORS, Typo } from "../../../../layout/theme";
import debounce from "lodash/debounce";
import CustomAntdTooltip from "../../../../common/custom-antd-tooltip/CustomAntdTooltip";
import moment from "moment";
import LeadStatusTag from "./components/LeadStatusTags";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import SecondaryButton from "../../../../common/antd-buttons/SecondaryButton";

export default function AgentConversations() {
	const { id: clientId } = useParams();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const [poolFilter, setPoolFilter] = useState("ALL");
	const [stateFilter, setStateFilter] = useState("ACTIVE");
	const [phaseFilter, setPhaseFilter] = useState(null);

	const [pageSize] = useState(CONSTANTS.PAGE_LARGE_SIZE);
	const [actualPage, setActualPage] = useState(1);

	const [rows, setRows] = useState([]);
	const [pageInfo, setPageInfo] = useState(null);

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedPos, setSelectedPos] = useState(null);

	const [convLoading, setConvLoading] = useState(false);
	const [convs, setConvs] = useState([]);
	const [convPage, setConvPage] = useState(1);
	const [convInfo, setConvInfo] = useState(null);

	const [msgsLoading, setMsgsLoading] = useState(false);
	const [msgs, setMsgs] = useState([]);
	const [selectedConversationId, setSelectedConversationId] = useState(null);
	const [msgPage, setMsgPage] = useState(1);
	const [msgInfo, setMsgInfo] = useState(null);

	const [phasesInfo, setPhasesInfo] = useState(null);
	const [search, setSearch] = useState("");
	const [sortLastConversation, setSortLastConversation] = useState("PDV");

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [listRefreshTick, setListRefreshTick] = useState(false);
	const [selectAllLoading, setSelectAllLoading] = useState(false);
	const [selectAllTotal, setSelectAllTotal] = useState(0);
	const selectedCount = selectedRowKeys.length;
	const isAllSelected = selectAllTotal > 0 && selectedCount === selectAllTotal;
	const selectedPosMapRef = React.useRef({});

	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [sendProgress, setSendProgress] = useState(0);
	const [sendStatus, setSendStatus] = useState("");
	const [sendDetails, setSendDetails] = useState("");
	const sendTimerRef = React.useRef(null);

	const [templatesLoading, setTemplatesLoading] = useState(false);
	const [templates, setTemplates] = useState([]);
	const [templateMap, setTemplateMap] = useState({});
	const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
	const [routeTagOptions, setRouteTagOptions] = useState([]);
	const [selectedRouteTag, setSelectedRouteTag] = useState(undefined);
	const [loadingRouteTags, setLoadingRouteTags] = useState(false);

	const fetchPage = async (search, pg = 1) => {
		try {
			setLoading(true);
			const res = await get_agent_pos_with_last_conv({
				clientId,
				poolFilter,
				search,
				state: stateFilter,
				phase: phaseFilter,
				routeTag: selectedRouteTag,
				sortLastConversation: sortLastConversation,
				page: pageSize,
				actualPage: pg,
			});
			setRows(res.items || []);
			const newMap = { ...selectedPosMapRef.current };
			(res.items || []).forEach((r) => {
				newMap[r.agentRouteId] = r.pointOfSaleId;
			});
			selectedPosMapRef.current = newMap;
			setPageInfo(res.paginationInfo || null);
			setActualPage(pg);
		} catch (e) {
			console.error(e);
			message.error("No se pudo cargar el listado de PDV.");
		} finally {
			setLoading(false);
		}
	};

	const fetchConversationPhases = async () => {
		try {
			setLoading(true);
			const res = await get_conversation_phases(clientId);
			setPhasesInfo(res.phases);
		} catch (error) {}
	};

	const fetchTemplates = async () => {
		if (!clientId) return;

		try {
			setTemplatesLoading(true);
			const list = await get_client_templates(clientId);

			setTemplates(list || []);

			const map = {};
			(list || []).forEach((t) => {
				map[t.name] = t;
			});
			setTemplateMap(map);
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar las plantillas del cliente.");
		} finally {
			setTemplatesLoading(false);
		}
	};

	useEffect(() => {
		fetchPage(search, 1);
		if (clientId) fetchConversationPhases();
		if (clientId) fetchTemplates();
		if (clientId) fetchRouteTags();
		setSelectedRowKeys([]);
		setSelectAllTotal(0);
	}, [
		clientId,
		poolFilter,
		stateFilter,
		phaseFilter,
		selectedRouteTag,
		sortLastConversation,
		listRefreshTick,
	]);

	useEffect(() => {
		return () => {
			debouncedEventHandler.cancel();
		};
	}, []);

	const eventHandler = (e) => {
		const v = e.target.value;
		setSearch(v);
		fetchPage(v, 1);
	};

	const debouncedEventHandler = useMemo(() => debounce(eventHandler, 600), []);

	const columns = useMemo(
		() => [
			{
				title: () => <div style={{ color: COLORS.White }}>Punto de Venta</div>,
				dataIndex: "pdvName",
				render: (v, r) => (
					<Space direction="vertical" size={0}>
						<Space>
							{r.isValidated ? (
								<Circle color={COLORS.Success} />
							) : (
								<CustomAntdTooltip
									width="250px"
									color={COLORS.Secondary}
									tooltipContent={[
										{
											title: "Punto de venta sin validar",
											body: `Click para redireccionar a validaciones`,
										},
									]}
								>
									<Circle
										color={COLORS.Warning}
										onClick={() =>
											navigate(`/admin/laboratoryAgentPool/${clientId}`)
										}
										style={{ cursor: "pointer" }}
									/>
								</CustomAntdTooltip>
							)}
							<Typography.Text strong>{v}</Typography.Text>
						</Space>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.address} • {r.province} • {r.location}
						</Typography.Text>
					</Space>
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Cuit</div>,
				dataIndex: "cuit",
				width: 140,
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Ultima fase</div>,
				dataIndex: "lastConversationPhase",
				width: 160,
				render: (v, r) =>
					v ? (
						r.lastConversationHasTicket ? (
							<Tag color="volcano">
								{CONSTANTS.AGENT_CONVERSATION["Done"]?.label}
							</Tag>
						) : (
							<Tag color={CONSTANTS.AGENT_CONVERSATION[v]?.color || "blue"}>
								{CONSTANTS.AGENT_CONVERSATION[v]?.label || "Indefinido"}
							</Tag>
						)
					) : (
						<Typography.Text type="secondary">—</Typography.Text>
					),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Lead</div>,
				dataIndex: "leadStatus",
				width: 180,
				render: (v, r) => (
					<LeadStatusTag status={r.leadStatus} reason={r.leadStatusReason} />
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Ultimo mensaje</div>,
				key: "lastmsg",
				render: (_, r) => (
					<Space direction="vertical" size={0}>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{r.lastMessageAt
								? moment.utc(r.lastMessageAt).local().format("DD/MM/YYYY HH:mm")
								: "—"}
						</Typography.Text>
						<div
							style={{
								maxWidth: 360,
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{r.lastMessagePreview || "—"}
						</div>
					</Space>
				),
			},
			{
				title: () => <div style={{ color: COLORS.White }}>Detalles</div>,
				key: "act",
				width: 120,
				render: (_, r) => (
					<Button
						type="link"
						onClick={() => openDrawer(r)}
						disabled={!r.pointOfSaleId || !r.lastMessagePreview}
					>
						Ver conversaciones
					</Button>
				),
			},
		],
		[],
	);

	const openDrawer = async (row) => {
		if (!row.pointOfSaleId) return message.info("PDV no validado.");
		setSelectedPos(row);
		setDrawerOpen(true);
		await loadConversations(row.pointOfSaleId, 1);
	};

	const loadConversations = async (
		pointOfSaleId,
		pg,
		opts = { keepSelection: false, keepMsgPage: false },
	) => {
		try {
			setConvLoading(true);
			const res = await get_pos_conversations_page({
				clientId,
				pointOfSaleId,
				page: CONSTANTS.PAGE_SMALL_SIZE,
				actualPage: pg,
			});
			setConvs(res.items || []);
			setConvInfo(res.paginationInfo || null);
			setConvPage(pg);
			const items = res.items || [];
			const first = items[0];

			const keep =
				opts.keepSelection &&
				selectedConversationId &&
				items.some((x) => x.id === selectedConversationId);

			const nextId = keep ? selectedConversationId : first?.id || null;
			setSelectedConversationId(nextId);

			if (nextId) {
				const nextMsgPage = opts.keepMsgPage ? msgPage : 1;
				await loadMessages(nextId, nextMsgPage);
			} else {
				setMsgs([]);
				setMsgInfo(null);
			}
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar conversaciones.");
		} finally {
			setConvLoading(false);
		}
	};

	const loadMessages = async (conversationId, pg) => {
		try {
			setMsgsLoading(true);
			const res = await get_conversation_messages_page({
				conversationId,
				page: CONSTANTS.PAGE_SMALL_SIZE,
				actualPage: pg,
			});
			setMsgs(res.items || []);
			setMsgInfo(res.paginationInfo || null);
			setMsgPage(pg);
		} catch (e) {
			console.error(e);
			message.error("No se pudieron cargar mensajes.");
		} finally {
			setMsgsLoading(false);
		}
	};

	const handleDecrement = (state, fetch, set) => {
		if (state.actualPage > 1) {
			const cp = state.actualPage - 1;
			fetch(search, cp);
			set(cp);
		}
	};

	const handleIncrement = (state, fetch, set) => {
		if (state.actualPage < state.totalPages) {
			const cp = state.actualPage + 1;
			fetch(search, cp);
			set(cp);
		}
	};

	const handlePageChange = (e, state, fetch, set) => {
		const selectedPage = parseInt(e.target.value);
		if (
			!isNaN(selectedPage) &&
			selectedPage >= 1 &&
			selectedPage <= state.totalPages
		) {
			fetch(search, selectedPage);
			set(selectedPage);
		} else {
			set(state.actualPage);
		}
	};

	const getColor = (status) => {
		switch (status) {
			case "OK":
				return COLORS.Success;
			case "Warning":
				return COLORS.Warning;
			case "Error":
				return COLORS.Danger;
			default:
				return COLORS.LightGrey;
		}
	};

	const rowSelection = useMemo(() => {
		const mergeKeys = (nextKeys) => setSelectedRowKeys(nextKeys);

		return {
			selectedRowKeys,
			preserveSelectedRowKeys: true,

			onSelect: (record, selected) => {
				const k = record.agentRouteId;

				setSelectedRowKeys((prev) => {
					if (selected) return prev.includes(k) ? prev : [...prev, k];
					return prev.filter((x) => x !== k);
				});
			},

			onSelectAll: (selected, selectedRows, changeRows) => {
				const keysChanged = changeRows.map((r) => r.agentRouteId);

				setSelectedRowKeys((prev) => {
					if (selected) {
						const set = new Set(prev);
						keysChanged.forEach((k) => set.add(k));
						return Array.from(set);
					}
					return prev.filter((k) => !keysChanged.includes(k));
				});
			},

			onChange: mergeKeys,

			getCheckboxProps: (record) => ({
				disabled: !record.pointOfSaleId,
			}),
		};
	}, [selectedRowKeys]);

	const refreshList = () => setListRefreshTick((p) => !p);

	const refreshDrawer = async () => {
		if (!selectedPos?.pointOfSaleId) return;
		await loadConversations(selectedPos.pointOfSaleId, convPage, {
			keepSelection: true,
			keepMsgPage: true,
		});
	};

	const refreshMessagesOnly = async () => {
		if (!selectedConversationId) return;
		await loadMessages(selectedConversationId, msgPage);
	};

	const leadSummary = useMemo(() => {
		const acc = {};
		(rows || []).forEach((r) => {
			const k = r.leadStatus || "—";
			acc[k] = (acc[k] || 0) + 1;
		});
		return acc;
	}, [rows]);

	const estimateSendSeconds = (n) => {
		// backend: sendBatchSize=200, maxConcurrency=4, delayBetweenBatches=0.5s
		// supuestos: cada envío ~0.35s promedio por request
		const perSend = 0.35;
		const concurrency = 4;
		const batch = 200;
		const delayBetweenBatches = 0.5;

		const batches = Math.max(1, Math.ceil(n / batch));
		const sendTime = (n * perSend) / concurrency;
		const delays = Math.max(0, batches - 1) * delayBetweenBatches;

		return Math.ceil((sendTime + delays) * 1.25);
	};

	const startFakeProgress = (total) => {
		if (sendTimerRef.current) clearInterval(sendTimerRef.current);

		const etaSec = estimateSendSeconds(total);
		const startedAt = Date.now();

		setSendProgress(2);
		setSendStatus("Preparando envío…");
		setSendDetails(`Destinatarios: ${total} • Tiempo estimado: ~${etaSec}s`);

		const phases = [
			{ at: 0.05, text: "Preparando envío…" },
			{ at: 0.18, text: "Validando destinatarios…" },
			{ at: 0.35, text: "Chequeando bajas (opt-out)..." },
			{ at: 0.55, text: "Enviando mensajes…" },
			{ at: 0.82, text: "Guardando registros…" },
			{ at: 0.93, text: "Finalizando…" },
		];

		sendTimerRef.current = setInterval(() => {
			const elapsed = (Date.now() - startedAt) / 1000;
			const t = Math.min(1, elapsed / etaSec);

			const eased = 1 - Math.pow(1 - t, 2.4);

			const next = Math.min(97, Math.floor(eased * 97));
			setSendProgress((p) => (next > p ? next : p));

			const pRel = next / 100;
			const phase = phases.reduce(
				(acc, cur) => (pRel >= cur.at ? cur : acc),
				phases[0],
			);
			setSendStatus(phase.text);

			const remaining = Math.max(0, etaSec - elapsed);
			setSendDetails(
				`Destinatarios: ${total} • Restante aprox.: ~${Math.ceil(remaining)}s`,
			);
		}, 250);
	};

	const stopFakeProgress = () => {
		if (sendTimerRef.current) {
			clearInterval(sendTimerRef.current);
			sendTimerRef.current = null;
		}
	};

	useEffect(() => {
		return () => stopFakeProgress();
	}, []);

	const fetchRouteTags = async () => {
		try {
			setLoadingRouteTags(true);

			const res = await get_agent_client_route_tags(clientId);

			setRouteTagOptions(
				(res || []).map((item) => ({
					value: item.value,
					label: item.label,
				})),
			);
		} catch (error) {
			console.error(error);
			message.error("No se pudieron cargar los tags internos.");
		} finally {
			setLoadingRouteTags(false);
		}
	};

	return (
		<Inner>
			<Row gutter={[12, 12]}>
				<Col span={24}>
					<Card title="Conversaciones del agente">
						<Space
							direction="vertical"
							style={{ marginBottom: 12, width: "100%" }}
						>
							<FilterContainer>
								<AntdSearch
									placeholder="Buscar por nombre o CUIT"
									allowClear
									onChange={debouncedEventHandler}
									style={{ width: 280, borderRadius: 15, height: 32 }}
								/>
								<Container>
									<Tag
										color="default"
										style={{ cursor: "pointer", borderRadius: 5 }}
										onClick={() => setPhaseFilter(null)}
									>
										Todos ({pageInfo?.totalRecords})
									</Tag>
									{phasesInfo?.map((p, idx) => (
										<Tag
											key={`${p.phase}_${idx}`}
											color={
												CONSTANTS.AGENT_CONVERSATION[p.phase]?.color || "blue"
											}
											style={{ cursor: "pointer", borderRadius: 5 }}
											onClick={() => {
												setPhaseFilter(p.phase);
												setActualPage(1);
											}}
										>
											{CONSTANTS.AGENT_CONVERSATION[p.phase]?.label || ""} (
											{p.count})
										</Tag>
									))}
								</Container>
								<FilterActions>
									<DefaultButton
										loading={templatesLoading}
										onClick={async () => {
											try {
												setTemplatesLoading(true);
												await sync_client_templates(clientId);
												await fetchTemplates();
												message.success("Plantillas sincronizadas.");
											} catch (e) {
												console.error(e);
												message.error("No se pudo sincronizar plantillas.");
											} finally {
												setTemplatesLoading(false);
											}
										}}
										style={{
											height: "32px",
											border: "1px solid grey",
										}}
									>
										Sincronizar plantillas
									</DefaultButton>
								</FilterActions>
							</FilterContainer>
							<FilterContainer style={{ gap: 6 }}>
								<Select
									placeholder="Plantilla"
									style={{ width: 280 }}
									value={selectedTemplate}
									onChange={(v) => {
										setSelectedTemplate(v);
									}}
									loading={templatesLoading}
									showSearch
									optionFilterProp="label"
									options={(templates || [])
										.filter((t) => !t.isDeletedInMeta)
										.map((t) => ({
											value: t.name,
											label: `${t.displayName}`,
										}))}
								/>
								<DefaultButton
									loading={selectAllLoading}
									style={{
										height: "32px",
										border: "1px solid grey",
										fontWeight: 400,
									}}
									onClick={async () => {
										if (isAllSelected) {
											setSelectedRowKeys([]);
											setSelectAllTotal(0);
											return;
										}

										try {
											setSelectAllLoading(true);

											const res = await get_agent_pos_selection_ids({
												clientId,
												poolFilter,
												search: "",
												state: stateFilter,
												phase: phaseFilter,
											});

											const allKeys = res.agentRouteIds || [];
											setSelectedRowKeys(allKeys);
											setSelectAllTotal(res.total ?? allKeys.length);

											selectedPosMapRef.current = {
												...selectedPosMapRef.current,
												...(res.map || {}),
											};

											if (allKeys.length === 0) {
												message.info(
													"No hay PDV validados para seleccionar con los filtros actuales.",
												);
											}
										} catch (e) {
											console.error(e);
											message.error("No se pudo seleccionar todos los PDV.");
										} finally {
											setSelectAllLoading(false);
										}
									}}
								>
									{isAllSelected ? "Deseleccionar todos" : "Seleccionar todos"}
								</DefaultButton>
								<SecondaryButton
									disabled={!selectedTemplate}
									onClick={() => setTemplatePreviewOpen(true)}
									style={{ height: 32, fontWeight: 400 }}
								>
									Vista previa plantilla
								</SecondaryButton>
								<PrimaryButton
									type="primary"
									disabled={!selectedTemplate || selectedRowKeys.length === 0}
									style={{ height: 32, fontWeight: 400 }}
									onClick={async () => {
										if (!selectedTemplate) {
											message.warning("Seleccioná una plantilla.");
											return;
										}
										if (selectedRowKeys.length === 0) {
											message.warning("Seleccioná al menos un punto de venta.");
											return;
										}

										const pointOfSaleIds = selectedRowKeys
											.map((k) => selectedPosMapRef.current[k])
											.filter(Boolean);

										if (pointOfSaleIds.length === 0) {
											message.warning(
												"Los PDV seleccionados no están validados.",
											);
											return;
										}

										try {
											setLoading(true);

											setSendModalOpen(true);
											startFakeProgress(pointOfSaleIds.length);

											const result = await send_template_to_pos({
												clientId,
												templateName: selectedTemplate,
												pointOfSaleIds,
											});

											setSendStatus("¡Listo! Plantilla enviada.");
											setSendDetails(
												result || `Destinatarios: ${pointOfSaleIds.length}`,
											);
											setSendProgress(100);

											message.success(
												result || "Plantilla enviada a los PDV seleccionados.",
											);

											setTimeout(() => {
												stopFakeProgress();
												setSendModalOpen(false);
												setSendProgress(0);
											}, 650);
										} catch (e) {
											console.error(e);
											stopFakeProgress();

											setSendStatus("Ocurrió un error enviando la plantilla.");
											setSendDetails(
												"Reintentá en unos segundos. Si persiste, revisá logs del servidor.",
											);
											setSendProgress(0);

											message.error("No se pudo enviar la plantilla.");

											setTimeout(() => {
												setSendModalOpen(false);
											}, 1200);
										} finally {
											setLoading(false);
											setSelectedTemplate(null);
										}
									}}
								>
									Enviar plantilla
								</PrimaryButton>
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<Button
										type="text"
										size="small"
										icon={<ReloadOutlined />}
										onClick={refreshList}
										loading={loading}
									/>
								</div>
								<Typography.Text type="secondary" style={{ marginLeft: 8 }}>
									Seleccionados: <b>{selectedCount}</b>
									{selectAllTotal > 0 ? (
										<>
											{" "}
											de <b>{selectAllTotal}</b>
										</>
									) : null}
								</Typography.Text>
								<FilterActions>
									{routeTagOptions && routeTagOptions?.length > 0 && (
										<Select
											allowClear
											showSearch
											placeholder="Filtrar por tag"
											style={{ width: 160 }}
											value={selectedRouteTag}
											onChange={(v) => {
												setSelectedRouteTag(v);
											}}
											options={routeTagOptions}
											loading={loadingRouteTags}
											optionFilterProp="label"
										/>
									)}
									<Select
										style={{ width: 220 }}
										value={sortLastConversation}
										onChange={(v) => setSortLastConversation(v)}
										options={[
											{
												value: "PDV",
												label: "Orden: nombre PDV",
											},
											{
												value: "DESC",
												label: "Orden: más recientes",
											},
											{
												value: "ASC",
												label: "Orden: más antiguas",
											},
										]}
									/>
								</FilterActions>
							</FilterContainer>
						</Space>

						<AntdTable
							loading={loading}
							dataSource={rows}
							columns={columns}
							rowKey={(r) => r.agentRouteId}
							pagination={false}
							rowSelection={rowSelection}
						/>
						{pageInfo && pageInfo.totalRecords > 0 && (
							<CompaginationDiv>
								{pageInfo.actualPage !== 1 && (
									<Button
										size="small"
										shape="circle"
										onClick={() =>
											handleDecrement(pageInfo, fetchPage, setActualPage)
										}
									>
										<LeftOutlined />
									</Button>
								)}
								<AntdInput
									step={1}
									min={1}
									max={pageInfo.totalPages}
									onPressEnter={(e) =>
										handlePageChange(e, pageInfo, fetchPage, setActualPage)
									}
									onChange={(e) => setActualPage(e.target.value.toString())}
									defaultValue={actualPage.toString()}
									value={actualPage.toString()}
									addonAfter={`/ ${pageInfo.totalPages}`}
									style={{
										width: ` ${pageInfo.totalPages > 99 ? "76px" : "66px"}`,
									}}
								/>
								{pageInfo.actualPage < pageInfo.totalPages && (
									<Button
										size="small"
										shape="circle"
										onClick={() =>
											handleIncrement(pageInfo, fetchPage, setActualPage)
										}
										disabled={
											pageInfo.actualPage === pageInfo.totalPages ? true : false
										}
									>
										<RightOutlined />
									</Button>
								)}
							</CompaginationDiv>
						)}
					</Card>
				</Col>

				<Drawer
					open={drawerOpen}
					width={900}
					onClose={() => {
						setDrawerOpen(false);
						setSelectedPos(null);
					}}
					title={
						selectedPos
							? `Conversaciones • ${selectedPos.pdvName}`
							: "Conversaciones"
					}
				>
					{/* <ConversationsStatsWidget convs={convs} /> */}
					{/* {selectedConversationId ? (
						<div style={{ marginBottom: 12 }}>
							<ConversationMetricsBar
								data={{
									...(convs.find((x) => x.id === selectedConversationId) || {}),
								}}
							/>
						</div>
					) : null} */}
					<Row gutter={[12, 12]}>
						<Col span={10}>
							<Card
								title={
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Typo level={6}>Historial de conversaciones</Typo>
										<Button
											type="text"
											size="small"
											icon={<ReloadOutlined />}
											onClick={refreshDrawer}
											loading={loading}
										/>
									</div>
								}
								size="small"
							>
								<CardContainer>
									<List
										loading={convLoading}
										dataSource={convs}
										renderItem={(c) => (
											<List.Item
												onClick={async () => {
													setSelectedConversationId(c.id);
													await loadMessages(c.id, 1);
												}}
												style={{
													cursor: "pointer",
													background:
														c.id === selectedConversationId
															? "#fafafa"
															: "transparent",
												}}
											>
												<List.Item.Meta
													title={
														<Space>
															<Tag color={c.isFinished ? "default" : "green"}>
																{c.isFinished ? "Finalizada" : "Activa"}
															</Tag>
															<Typography.Text>{c.phase}</Typography.Text>
															<LeadStatusTag
																status={c.leadStatus}
																reason={c.leadStatusReason}
															/>
															{c.hasTicket && (
																<Tag color="volcano">Abrió Ticket</Tag>
															)}
														</Space>
													}
													description={
														<Space direction="vertical" size={0}>
															<Typography.Text
																type="secondary"
																style={{ fontSize: 12 }}
															>
																{moment
																	.utc(c.updatedAt)
																	.local()
																	.format("DD/MM/YYYY HH:mm")}
															</Typography.Text>
															<div
																style={{
																	maxWidth: 300,
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{c.lastMessagePreview}
															</div>
														</Space>
													}
												/>
											</List.Item>
										)}
									/>
									{convInfo && (
										<div
											style={{
												marginTop: 8,
												display: "flex",
												gap: 8,
												alignItems: "center",
											}}
										>
											<Button
												size="small"
												shape="circle"
												disabled={convInfo.actualPage <= 1}
												onClick={() =>
													loadConversations(
														selectedPos.pointOfSaleId,
														convPage - 1,
													)
												}
											>
												<LeftOutlined />
											</Button>
											<span>
												{convInfo.actualPage}/{convInfo.totalPages}
											</span>
											<Button
												size="small"
												shape="circle"
												disabled={convInfo.actualPage >= convInfo.totalPages}
												onClick={() =>
													loadConversations(
														selectedPos.pointOfSaleId,
														convPage + 1,
													)
												}
											>
												<RightOutlined />
											</Button>
										</div>
									)}
								</CardContainer>
							</Card>
						</Col>
						<Col span={14}>
							<Card
								title={
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
										}}
									>
										<Typo level={6}>Mensajes</Typo>
										<Button
											type="text"
											size="small"
											icon={<ReloadOutlined />}
											onClick={refreshMessagesOnly}
											loading={loading}
										/>
									</div>
								}
								size="small"
							>
								<CardContainer>
									<List
										loading={msgsLoading}
										dataSource={msgs}
										renderItem={(m) => (
											<List.Item>
												<List.Item.Meta
													title={
														<Space>
															<Tag
																color={
																	CONSTANTS.AGENT_CONVERSATION[m.role]?.color ||
																	"blue"
																}
															>
																{CONSTANTS.AGENT_CONVERSATION[m.role]?.label ||
																	""}
															</Tag>
															<Typography.Text
																type="secondary"
																style={{ fontSize: 12 }}
															>
																{moment
																	.utc(m.created)
																	.local()
																	.format("DD-MM-YYYY HH:mm:ss")}
															</Typography.Text>
														</Space>
													}
													description={
														<div style={{ whiteSpace: "pre-wrap" }}>
															{m.content}
														</div>
													}
												/>
											</List.Item>
										)}
									/>
									{msgInfo && (
										<div
											style={{
												marginTop: 8,
												display: "flex",
												gap: 8,
												alignItems: "center",
											}}
										>
											<Button
												size="small"
												shape="circle"
												disabled={msgInfo.actualPage <= 1}
												onClick={() =>
													loadMessages(selectedConversationId, msgPage - 1)
												}
											>
												<LeftOutlined />
											</Button>
											<span>
												{msgInfo.actualPage}/{msgInfo.totalPages}
											</span>
											<Button
												size="small"
												shape="circle"
												disabled={msgInfo.actualPage >= msgInfo.totalPages}
												onClick={() =>
													loadMessages(selectedConversationId, msgPage + 1)
												}
											>
												<RightOutlined />
											</Button>
										</div>
									)}
								</CardContainer>
							</Card>
						</Col>
					</Row>
				</Drawer>
			</Row>

			<Modal
				open={sendModalOpen}
				title="Enviando plantilla"
				closable={false}
				maskClosable={false}
				footer={null}
			>
				<Space direction="vertical" style={{ width: "100%" }} size={12}>
					<Typography.Text>{sendStatus}</Typography.Text>

					<Progress percent={sendProgress} />

					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{sendDetails}
					</Typography.Text>

					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						No cierres esta ventana. El envío puede demorar según la cantidad de
						destinatarios.
					</Typography.Text>
				</Space>
			</Modal>

			<Modal
				open={templatePreviewOpen}
				onCancel={() => setTemplatePreviewOpen(false)}
				title="Vista previa de plantilla"
				footer={null}
			>
				{(() => {
					const t = templateMap[selectedTemplate];
					if (!t) return <Typography.Text type="secondary">—</Typography.Text>;

					return (
						<Space direction="vertical" style={{ width: "100%" }}>
							<Space wrap>
								<Tag>Categoría: {t.category}</Tag>
								<Tag>Idioma: {t.language}</Tag>
								<Tag color={t.status === "APPROVED" ? "green" : "orange"}>
									Estado: {t.status}
								</Tag>
							</Space>
							<div style={{ marginTop: 5, marginBottom: 5 }}>
								<Typography.Text strong>Nombre clave: {t.name}</Typography.Text>
							</div>
							<div
								style={{
									background: "#fafafa",
									border: "1px solid #eee",
									borderRadius: 8,
									padding: 12,
									whiteSpace: "pre-wrap",
									fontFamily: "monospace",
								}}
							>
								{t.previewFull || t.previewBody || "—"}
							</div>
						</Space>
					);
				})()}
			</Modal>
		</Inner>
	);
}
